import { ReactNode, Dispatch, SetStateAction, memo } from 'react';
import clsx from 'clsx';
import { BigNumber, utils } from 'ethers';
import { Icon, Table, Text, Tooltip } from '@cronos-labs/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';

import { MarketsSliceState } from '@redux/markets';
import { UsdPricesSliceState } from '@redux/usdPrices';
import { getUtilizationRate, getUnderlyingUsdValue } from '@lib/math';
import { getUsdPriceDecimals } from '@lib/units';

export interface MarketsTableHeaderColumn {
  title: string;
  key: MarketsTableColumKey;
  align?: 'right' | 'center' | 'left';
  cellClassName?: string;
  tooltip?: string;
}

interface TableHeaderCellProps {
  align?: 'right' | 'center' | 'left';
  children: ReactNode;
  className?: string;
}

function TableHeaderCell({
  align,
  children,
  className,
}: TableHeaderCellProps): JSX.Element {
  return (
    <Table.HeaderCell
      align={align}
      as="div"
      className={clsx('table-cell align-middle', className)}
      role="columnheader"
    >
      {children}
    </Table.HeaderCell>
  );
}

export enum MarketsTableColumKey {
  default = 'default',
  markets = 'markets',
  totalSupply = 'totalSupply',
  totalBorrow = 'totalBorrow',
  utilization = 'utilization',
  supplyApy = 'supplyApy',
  borrowApy = 'borrowApy',
}

const columns: MarketsTableHeaderColumn[] = [
  {
    title: 'Markets',
    key: MarketsTableColumKey.markets,
    align: 'left',
    cellClassName: 'mobile:w-1/2',
  },
  {
    title: 'Total supply',
    key: MarketsTableColumKey.totalSupply,
    align: 'right',
    cellClassName: 'mobile:hidden',
  },
  {
    title: 'Total borrow',
    key: MarketsTableColumKey.totalBorrow,
    align: 'right',
    cellClassName: 'mobile:hidden',
  },
  {
    title: 'Utilization',
    key: MarketsTableColumKey.utilization,
    tooltip: 'The percentage of assets lent out to borrowers',
    align: 'center',
    cellClassName: 'tablet:hidden mobile:hidden',
  },
  {
    title: 'Net Supply APY',
    key: MarketsTableColumKey.supplyApy,
    align: 'right',
    cellClassName: 'mobile:w-1/4',
  },
  {
    title: 'Net Borrow APY',
    key: MarketsTableColumKey.borrowApy,
    align: 'right',
    cellClassName: 'mobile:w-1/4',
  },
];

interface MarketsTableHeaderProps {
  orderStatus: {
    activeKey: string;
    sortOrder: string;
  };
  handleOrderStatus: Dispatch<
    SetStateAction<{ activeKey: MarketsTableColumKey; sortOrder: string }>
  >;
}

function MarketsTableHeader({
  orderStatus,
  handleOrderStatus,
}: MarketsTableHeaderProps): JSX.Element {
  const handleOrderChange = (column: MarketsTableHeaderColumn) => {
    const { key: curKey } = column;

    const nextSortOrder =
      orderStatus.activeKey === curKey
        ? orderStatus.sortOrder === 'ASCEND'
          ? ''
          : 'ASCEND'
        : 'DESCEND';

    const key = nextSortOrder ? curKey : MarketsTableColumKey.default;
    const order = nextSortOrder;

    handleOrderStatus({
      activeKey: key,
      sortOrder: order,
    });
  };

  return (
    <Table.Header
      as="div"
      className="table-header-group"
      role="rowgroup"
      data-testid="tableHeaderGroup"
    >
      <Table.HeaderRow as="div" className="table-row" role="row">
        {columns.map((column: MarketsTableHeaderColumn) => {
          const { cellClassName, title, key, align, tooltip } = column;

          return (
            <TableHeaderCell
              className={`${cellClassName} last:pr-3`}
              key={key}
              align={align}
            >
              <div
                className={clsx(
                  'relative flex items-center',
                  align === 'left' && 'justify-start',
                  align === 'right' && 'justify-end',
                  align === 'center' && 'mx-auto justify-end'
                )}
                onClick={() => handleOrderChange(column)}
              >
                <Text
                  as="h2"
                  className={clsx(
                    'cursor-pointer select-none',
                    align === 'center' && 'mr-0.5'
                  )}
                >
                  {title}
                </Text>
                {tooltip && (
                  <Tooltip message={tooltip}>
                    <Icon.Question className="h-3 w-3" />
                  </Tooltip>
                )}
                {orderStatus.activeKey === key && (
                  <Icon.ChevronDown
                    className={clsx(
                      'absolute cursor-pointer select-none text-yellowPrimary',
                      orderStatus.sortOrder === 'ASCEND' && 'rotate-180',
                      align === 'left' ? 'left-9' : '-right-2.5'
                    )}
                  />
                )}
              </div>
            </TableHeaderCell>
          );
        })}
      </Table.HeaderRow>
    </Table.Header>
  );
}

export default memo(MarketsTableHeader, (prev, next) => {
  return prev.orderStatus === next.orderStatus;
});

export const getAmountInUsdCollections = (
  amountCollections:
    | MarketsSliceState['borrowAmounts']
    | MarketsSliceState['supplyAmounts'],
  usdPricesCollections: UsdPricesSliceState['bySymbol'],
  supportedAssetsMap: Record<string, TectonicAsset>
) => {
  if (!amountCollections || !usdPricesCollections) return {};

  return Object.keys(amountCollections).reduce(
    (p: Record<string, number>, symbol) => {
      const asset = supportedAssetsMap[symbol];
      const amount = amountCollections[symbol]
        ? BigNumber.from(amountCollections[symbol])
        : null;
      const assetUsdPrice = usdPricesCollections[symbol]
        ? BigNumber.from(usdPricesCollections[symbol])
        : null;

      if (!asset || !amount || !assetUsdPrice) return p;

      const usdValue = getUnderlyingUsdValue(asset, amount, assetUsdPrice);

      p[symbol] = +utils.formatUnits(usdValue, getUsdPriceDecimals(asset));
      return p;
    },
    {}
  );
};

export const getUtilizationRateCollections = (
  totalSupplyAmounts: MarketsSliceState['supplyAmounts'],
  totalBorrowAmounts: MarketsSliceState['borrowAmounts']
) => {
  if (
    Object.keys(totalBorrowAmounts).length ===
    Object.keys(totalSupplyAmounts).length
  ) {
    return Object.keys(totalBorrowAmounts).reduce(
      (p: Record<string, number>, symbol) => {
        const borrowAmount = totalBorrowAmounts[symbol]
          ? BigNumber.from(totalBorrowAmounts[symbol])
          : null;
        const supplyAmount = totalSupplyAmounts[symbol]
          ? BigNumber.from(totalSupplyAmounts[symbol])
          : null;

        if (!borrowAmount || !supplyAmount) return p;

        p[symbol] = getUtilizationRate(borrowAmount, supplyAmount);

        return p;
      },
      {}
    );
  }
  return {};
};

const compareHelper = (
  a: number | string,
  b: number | string,
  sortOrder: string
) => {
  if (typeof a === 'string' && typeof b === 'string') {
    return sortOrder === 'DESCEND' ? b.localeCompare(a) : a.localeCompare(b);
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return sortOrder === 'DESCEND' ? b - a : a - b;
  }
  return 0; // for type safe
};

export const mapOrder = (
  _source: TectonicAsset[],
  orderCollections: {
    [MarketsTableColumKey.markets]: Record<string, TectonicAsset>;
    [MarketsTableColumKey.totalSupply]: Record<string, number>;
    [MarketsTableColumKey.totalBorrow]: Record<string, number>;
    [MarketsTableColumKey.utilization]: Record<string, number>;
    [MarketsTableColumKey.supplyApy]: Record<string, number>;
    [MarketsTableColumKey.borrowApy]: Record<string, number>;
  },
  key: MarketsTableColumKey,
  sortOrder: string
) => {
  const source = [..._source];

  if (key === MarketsTableColumKey.default) return source;

  if (
    !orderCollections ||
    !orderCollections[key] ||
    Object.keys(orderCollections[key]).length > source.length
  )
    return source;

  switch (key) {
    case MarketsTableColumKey.markets: {
      source.sort((a, b) => compareHelper(a.symbol, b.symbol, sortOrder));

      break;
    }

    default: {
      const order = orderCollections[key];
      source.sort((a, b) =>
        compareHelper(order[a.symbol], order[b.symbol], sortOrder)
      );
    }
  }

  return source;
};
